import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { logErrorIfDebug } from "utils/logging";

import { isNotNullOrUndefined } from "newui/utils/objectUtil";

import type { FeatureFlagType } from "./types/FeatureFlag.type";
import type { TwoFactorAuthentication } from "./types/TwoFactorAuthentication.type";
import type { User } from "./types/User.type";
import type { UserCompanyProfile } from "./types/UserCompanyProfile.type";
import { CompanyProfileType } from "./types/CompanyProfileType";
import { getPlanTypeClientOrSupplier } from "./utils";
import { ReferralToken } from "./components/Login";

export interface GuestSwitch {
	company: UserCompanyProfile | null;
	is_loaded: boolean;
	url: string | null;
	user: User | null;
}

interface AuthState {
	currentCompany: UserCompanyProfile | null;
	currentProfileType: CompanyProfileType;
	guestSwitch: GuestSwitch;
	is_loading: boolean;
	referralToken: ReferralToken | null;
	signup_details: User;
	two_factor_enabled: boolean;
	two_factor_verified: boolean;
	user: User | null;
	user_feature_flags: FeatureFlagType[];
	user_roles: string[];
}

const initialState: AuthState = {
	user: null,
	user_feature_flags: [],
	user_roles: [],
	signup_details: null,
	currentCompany: null,
	currentProfileType: CompanyProfileType.NONE,
	two_factor_enabled: false,
	two_factor_verified: false,
	is_loading: false,
	guestSwitch: { is_loaded: false, company: null, user: null, url: null },
	referralToken: null,
};

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		authenticateUser: (
			state,
			action: PayloadAction<TwoFactorAuthentication>
		) => {
			state.two_factor_enabled = action.payload.two_factor_enabled;
		},
		authenticateTwoFactor: (
			state,
			action: PayloadAction<TwoFactorAuthentication>
		) => {
			state.two_factor_enabled = action.payload.two_factor_enabled;
			state.two_factor_verified = action.payload.two_factor_verified;
		},
		enableTwoFactor: (state) => {
			state.two_factor_enabled = true;
		},
		disableTwoFactor: (state) => {
			state.two_factor_enabled = false;
			state.two_factor_verified = false;
		},
		logoutUser: () => initialState,
		setCurrentUser: (state, action: PayloadAction<User>) => {
			state.user = action.payload;
			state.currentCompany = action.payload?.company;
			state.currentProfileType = getPlanTypeClientOrSupplier(
				action.payload?.company?.plan?.package_name
			);
		},
		setUserFeatureFlags: (state, action: PayloadAction<FeatureFlagType[]>) => {
			state.user_feature_flags = action.payload;
		},
		setUserRoles: (state, action: PayloadAction<string[]>) => {
			state.user_roles = action.payload;
		},

		/**
		 * Only use this if you are JUST updating the current company
		 * setCurrentUser automatically sets current Company on reload
		 * @param state
		 * @param action
		 */
		setCurrentCompany: (state, action: PayloadAction<UserCompanyProfile>) => {
			state.currentCompany = action.payload;
			state.currentProfileType = getPlanTypeClientOrSupplier(
				action.payload?.plan?.package_name
			);
		},

		/**
		 * This controls the Procurer or Bidder display view in the UI
		 * @param state
		 * @param action
		 */
		setCurrentProfileType: (
			state,
			action: PayloadAction<CompanyProfileType>
		) => {
			state.currentProfileType = action.payload;
		},
		setSignupDetails: (state, action: PayloadAction<User>) => {
			// Handle localStorage
			try {
				const existingData = localStorage.getItem("signup_data");
				const parsedData = existingData ? JSON.parse(existingData) : {};
				localStorage.setItem(
					"signup_data",
					JSON.stringify({ ...parsedData, ...action.payload })
				);
			} catch (error) {
				logErrorIfDebug("Failed to save signup data to localStorage:", error);
			}

			state.signup_details = { ...state.signup_details, ...action.payload };
		},
		setIsAuthLoading: (state, action: PayloadAction<boolean>) => {
			state.is_loading = action.payload;
		},
		setGuestSwitching: (
			state,
			action: PayloadAction<{ company?: UserCompanyProfile; user?: User }>
		) => {
			state.guestSwitch = {
				...action.payload,
				is_loaded:
					isNotNullOrUndefined(action.payload.company) &&
					isNotNullOrUndefined(action.payload.user),
			} as GuestSwitch;
		},
		setReferralToken: (state, action: PayloadAction<ReferralToken>) => {
			state.referralToken = action.payload;
		},
		clearReferralToken: (state) => {
			state.referralToken = null;
		},
	},
});

export const {
	authenticateUser,
	authenticateTwoFactor,
	enableTwoFactor,
	disableTwoFactor,
	logoutUser,
	setCurrentCompany,
	setCurrentUser,
	setCurrentProfileType,
	setGuestSwitching,
	setIsAuthLoading,
	setSignupDetails,
	setUserFeatureFlags,
	setUserRoles,
	setReferralToken,
	clearReferralToken,
} = authSlice.actions;

export default authSlice.reducer;
